import { c as _c } from "react/compiler-runtime";
import { useId } from 'react';
import { type FieldValues, type Path, type RegisterOptions, useFormContext, useFormState } from 'react-hook-form';
import type { CheckboxProps } from '../../checkbox';
import Checkbox from '../../checkbox';
import type { LabelTooltipProps } from '../../label';
import FormWrapper from '../internals/form-wrapper';
export type FormCheckboxOption = {
  label: string | React.ReactNode;
  value: string;
  hint?: string;
};
export type FormCheckboxOptions = FormCheckboxOption[];
export type FormCheckboxGroupProps<TFieldValues extends FieldValues> = {
  name: Path<TFieldValues>;
  options: FormCheckboxOptions;
  rules?: RegisterOptions<TFieldValues, Path<TFieldValues>>;
  errorPosition?: 'absolute' | 'relative';
  orientation?: 'vertical' | 'horizontal';
  addon?: React.ReactNode;
  tooltip?: LabelTooltipProps;
  className?: string;
} & Omit<CheckboxProps, 'checked' | 'defaultChecked' | 'name' | 'onBlur' | 'ref' | 'label' | 'hasError' | 'hint' | 'value'>;
export default function FormCheckbox(t0) {
  const $ = _c(51);
  let addon;
  let baseId;
  let checkboxProps;
  let className;
  let name;
  let onChange;
  let options;
  let rules;
  let t1;
  let t2;
  let tooltip;
  if ($[0] !== t0) {
    ({
      name,
      options,
      rules,
      id: baseId,
      errorPosition: t1,
      orientation: t2,
      addon,
      onChange,
      tooltip,
      className,
      ...checkboxProps
    } = t0);
    $[0] = t0;
    $[1] = addon;
    $[2] = baseId;
    $[3] = checkboxProps;
    $[4] = className;
    $[5] = name;
    $[6] = onChange;
    $[7] = options;
    $[8] = rules;
    $[9] = t1;
    $[10] = t2;
    $[11] = tooltip;
  } else {
    addon = $[1];
    baseId = $[2];
    checkboxProps = $[3];
    className = $[4];
    name = $[5];
    onChange = $[6];
    options = $[7];
    rules = $[8];
    t1 = $[9];
    t2 = $[10];
    tooltip = $[11];
  }
  const errorPosition = t1 === undefined ? "relative" : t1;
  const orientation = t2 === undefined ? "vertical" : t2;
  const {
    register,
    control,
    getFieldState
  } = useFormContext();
  let t3;
  if ($[12] !== checkboxProps.disabled || $[13] !== control || $[14] !== name) {
    t3 = {
      control,
      name,
      disabled: checkboxProps.disabled
    };
    $[12] = checkboxProps.disabled;
    $[13] = control;
    $[14] = name;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  const formState = useFormState(t3);
  let t4;
  if ($[16] !== formState || $[17] !== getFieldState || $[18] !== name) {
    t4 = getFieldState(name, formState);
    $[16] = formState;
    $[17] = getFieldState;
    $[18] = name;
    $[19] = t4;
  } else {
    t4 = $[19];
  }
  const {
    error
  } = t4;
  const contextId = useId();
  const id = baseId || contextId;
  const t5 = error?.message;
  const t6 = formState.disabled || checkboxProps.disabled;
  let t7;
  if ($[20] !== checkboxProps || $[21] !== error || $[22] !== formState.disabled || $[23] !== id || $[24] !== name || $[25] !== onChange || $[26] !== options || $[27] !== register || $[28] !== rules) {
    let t8;
    if ($[30] !== checkboxProps || $[31] !== error || $[32] !== formState.disabled || $[33] !== id || $[34] !== name || $[35] !== onChange || $[36] !== register || $[37] !== rules) {
      t8 = t9 => {
        const {
          label,
          value,
          hint
        } = t9;
        const {
          onChange: reactHookFormOnChange,
          ...rest
        } = register(name, {
          ...rules
        });
        return <Checkbox key={value} {...checkboxProps} {...rest} onChange={e => {
          reactHookFormOnChange(e);
          onChange?.(e);
        }} disabled={formState.disabled || checkboxProps.disabled} hasError={!!error} id={`${id}-${value}`} data-dd-privacy="mask" label={label} hint={hint} value={value} />;
      };
      $[30] = checkboxProps;
      $[31] = error;
      $[32] = formState.disabled;
      $[33] = id;
      $[34] = name;
      $[35] = onChange;
      $[36] = register;
      $[37] = rules;
      $[38] = t8;
    } else {
      t8 = $[38];
    }
    t7 = options.map(t8);
    $[20] = checkboxProps;
    $[21] = error;
    $[22] = formState.disabled;
    $[23] = id;
    $[24] = name;
    $[25] = onChange;
    $[26] = options;
    $[27] = register;
    $[28] = rules;
    $[29] = t7;
  } else {
    t7 = $[29];
  }
  let t8;
  if ($[39] !== className || $[40] !== t7) {
    t8 = <div className={className}>{t7}</div>;
    $[39] = className;
    $[40] = t7;
    $[41] = t8;
  } else {
    t8 = $[41];
  }
  let t9;
  if ($[42] !== addon || $[43] !== errorPosition || $[44] !== id || $[45] !== orientation || $[46] !== t5 || $[47] !== t6 || $[48] !== t8 || $[49] !== tooltip) {
    t9 = <FormWrapper id={id} error={t5} errorPosition={errorPosition} addon={addon} size="default" orientation={orientation} fullWidth={true} disabled={t6} tooltip={tooltip}>{t8}</FormWrapper>;
    $[42] = addon;
    $[43] = errorPosition;
    $[44] = id;
    $[45] = orientation;
    $[46] = t5;
    $[47] = t6;
    $[48] = t8;
    $[49] = tooltip;
    $[50] = t9;
  } else {
    t9 = $[50];
  }
  return t9;
}